import Grid from "@mui/material/Grid2";
import Popover from "@mui/material/Popover";
import * as React from "react";
import { SearchResponseModel } from "../../../models/SearchModels";
import { useCompleteChasables } from "../../../hooks/useCompleteChasables";
import { useHandleChase } from "../../../hooks/useHandleChase";
import { useCustomer } from "../../../hooks/useCustomer";
import { useOutlook } from "../../../hooks/useOutlook";
import { Footer } from "../../../components/Footer";
import { ChaseButton } from "../../../components/ChaseButton";
import { CompleteButton } from "../../../components/CompleteButton";
import { AdjustToneList } from "../../../components/AdjustToneList";
import { BackButton } from "../../../components/BackButton";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { OrganisationFilePicker } from "../../../components/OrganisationFilePicker/OrganisationFilePicker";
import { hasOtherContacts } from "../../../utils/contactRecipients";

export function SearchFooter({
  selectedInvoiceIds,
  value,
  showBackButton,
}: {
  selectedInvoiceIds: string[];
  value: SearchResponseModel;
  showBackButton: boolean;
}) {
  const navigate = useNavigate();
  const [organisationFilePickerAnchor, setOrganisationFilePickerAnchor] = useState<null | HTMLElement>(null);
  const [adjustToneAnchor, setAdjustToneAnchor] = useState<null | HTMLElement>(null);
  const { isInComposeMode } = useOutlook();
  const { completeChasables, isLoading: isCompleteLoading } = useCompleteChasables();
  const customerId = value.type === "Contact" ? value.id : undefined;
  const { customer } = useCustomer(customerId);
  const {
    handleChase,
    handleDraftFromTemplate,
    addOtherContactsToCc,
    isLoading: isChaseLoading,
    hasBeenChased,
    resetHasBeenChased,
  } = useHandleChase(
    value.type === "Contact"
      ? { customerId: value.id, invoiceIds: selectedInvoiceIds, contacts: customer?.contacts }
      : { invoiceIds: selectedInvoiceIds[0] }
  );

  React.useEffect(() => {
    resetHasBeenChased();
  }, [selectedInvoiceIds]);

  return (
    <Footer>
      {showBackButton && (
        <Grid size={6}>
          <BackButton onClick={() => navigate(-1)} />
        </Grid>
      )}
      <Grid size={showBackButton ? 6 : "grow"}>
        {!hasBeenChased ? (
          <ChaseButton
            onClick={(languageId, recipientMode) => handleChase(undefined, undefined, languageId, recipientMode)}
            loading={isChaseLoading}
            disabled={!selectedInvoiceIds.length || isChaseLoading}
            contacts={customer?.contacts}
            invoiceCount={selectedInvoiceIds.length}
            onOpenAttachPickerForDraft={(anchorEl) => setOrganisationFilePickerAnchor(anchorEl)}
            onEmailTemplateClick={(template, recipientMode) => handleDraftFromTemplate(template, recipientMode)}
          />
        ) : (
          <CompleteButton
            onClick={() => completeChasables(selectedInvoiceIds)}
            loading={isCompleteLoading || isChaseLoading}
            disabled={!selectedInvoiceIds.length || isCompleteLoading || isChaseLoading}
            onOpenAdjustTone={hasBeenChased ? (anchorEl) => setAdjustToneAnchor(anchorEl) : undefined}
            onAddOtherContactsToCc={
              hasBeenChased && customer?.contacts && hasOtherContacts(customer.contacts)
                ? addOtherContactsToCc
                : undefined
            }
          />
        )}
      </Grid>
      <OrganisationFilePicker
        open={!!organisationFilePickerAnchor}
        anchorEl={organisationFilePickerAnchor}
        onClose={() => setOrganisationFilePickerAnchor(null)}
        onAdd={(files) => {
          handleChase(undefined, files);
          setOrganisationFilePickerAnchor(null);
        }}
        alreadySelectedIds={[]}
      />
      <Popover
        open={!!adjustToneAnchor}
        anchorEl={adjustToneAnchor}
        onClose={() => setAdjustToneAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <AdjustToneList
          isLoading={isChaseLoading}
          onSelectTone={(toneId) => {
            handleChase(toneId);
            setAdjustToneAnchor(null);
          }}
        />
      </Popover>
    </Footer>
  );
}
